import type { SajuAnalysis } from '../types/input';
import type { YongSinCompatibility } from '../types/compatibility';

const THRESHOLD = 0.15; // 15%+ 비율이면 보완으로 간주

/**
 * 두 사주의 용신 호환성을 분석한다.
 *
 * - person1의 용신 오행이 person2의 오행 분포에서 15%+ → 보완
 * - 역방향도 동일 확인
 * - 양쪽 다 보완 = excellent, 한쪽만 = good, 없음 = neutral
 * - 용신이 상대의 기신과 같으면 = conflicting
 */
export function analyzeYongSinCompatibility(
  analysis1: SajuAnalysis,
  analysis2: SajuAnalysis,
): YongSinCompatibility {
  const bestMethod1 = analysis1.yongSinMethods[0];
  const bestMethod2 = analysis2.yongSinMethods[0];
  const yongSin1 = bestMethod1.yongSin;
  const yongSin2 = bestMethod2.yongSin;
  const giSin1 = bestMethod1.giSin;
  const giSin2 = bestMethod2.giSin;

  const dist1 = analysis1.ohaengDistribution;
  const dist2 = analysis2.ohaengDistribution;

  // ohaengDistribution은 절대값(합계 ~8-12)이므로 비율로 변환하여 비교
  const total1 = Object.values(dist1).reduce((s, v) => s + v, 0);
  const total2 = Object.values(dist2).reduce((s, v) => s + v, 0);

  // person1의 용신이 person2 분포에서 충분한 비율인지
  const p1InP2 = total2 > 0 && (dist2[yongSin1] / total2) >= THRESHOLD;
  // person2의 용신이 person1 분포에서 충분한 비율인지
  const p2InP1 = total1 > 0 && (dist1[yongSin2] / total1) >= THRESHOLD;

  // 충돌 체크: 용신이 상대의 기신과 같은 경우
  const conflict1 = yongSin1 === giSin2;
  const conflict2 = yongSin2 === giSin1;
  const hasConflict = conflict1 || conflict2;

  let mutualSupport: YongSinCompatibility['mutualSupport'];
  if (hasConflict) {
    mutualSupport = '충돌';
  } else if (p1InP2 && p2InP1) {
    mutualSupport = '상호보완';
  } else if (p1InP2 || p2InP1) {
    mutualSupport = '부분보완';
  } else {
    mutualSupport = '중립';
  }

  const supportLabel: Record<YongSinCompatibility['mutualSupport'], string> = {
    '상호보완': '상호 보완 (매우 좋음)',
    '부분보완': '부분 보완 (좋음)',
    '중립': '중립',
    '충돌': '용신-기신 충돌',
  };

  const description = `용신 호환: ${supportLabel[mutualSupport]} (갑: ${yongSin1}, 을: ${yongSin2})`;

  return {
    person1YongSinInPerson2: p1InP2,
    person2YongSinInPerson1: p2InP1,
    mutualSupport,
    description,
  };
}
